/**
 * Path references appear in provider output as bare paths, backticked paths, and
 * markdown link targets. They routinely point outside the task's repository, so
 * parsing must keep the exact path a provider printed rather than guessing at a
 * repo-relative match.
 */

import { getPlatformInfo } from "../../nestAPIs/platform"
import { splitPath } from "./paths"

const LABEL_MAX_SEGMENTS = 5
const LABEL_TAIL_SEGMENTS = 3
const IMAGE_EXTENSIONS = new Set(["apng", "avif", "bmp", "gif", "ico", "jpeg", "jpg", "png", "svg", "webp"])
const PDF_EXTENSIONS = new Set(["pdf"])

/**
 * Nest detects media types from the extension first, so the same test lets the
 * client skip an inline content read it would only discard for a streamed preview.
 */
export function hasImageExtension(path: string): boolean {
    const extension = /\.([A-Za-z0-9]+)$/.exec(path)?.[1]
    return extension ? IMAGE_EXTENSIONS.has(extension.toLowerCase()) : false
}

export function hasPdfExtension(path: string): boolean {
    const extension = /\.([A-Za-z0-9]+)$/.exec(path)?.[1]
    return extension ? PDF_EXTENSIONS.has(extension.toLowerCase()) : false
}

export interface PathReference {
    path: string
    line: number | null
}

export function isAbsolutePathReference(path: string): boolean {
    return path.startsWith("/") || path.startsWith("\\") || /^[A-Za-z]:[/\\]/.test(path)
}

export function isHomeRelativePathReference(path: string): boolean {
    return path === "~" || path.startsWith("~/") || path.startsWith("~\\")
}

/**
 * Expands a leading ~ so every downstream consumer compares and opens the same
 * absolute path. Nest expands it too, but the renderer needs it to decide
 * whether a reference is inside the working directory.
 */
export function expandHomePathReference(path: string): string {
    if (!isHomeRelativePathReference(path)) return path
    const { homeDir } = getPlatformInfo()
    if (!homeDir || homeDir === "/") return path
    const rest = path.slice(1).replace(/^[/\\]/, "")
    return rest ? `${trimTrailingSeparator(homeDir)}/${rest}` : homeDir
}

function trimTrailingSeparator(path: string): string {
    return path.length > 1 ? path.replace(/[/\\]+$/, "") : path
}

function normalizeForComparison(path: string): string {
    return trimTrailingSeparator(path.replace(/\\/g, "/"))
}

function relativeFromNormalizedRoot(root: string, candidate: string): string | null {
    if (candidate === root) return "."
    const prefix = root.endsWith("/") ? root : `${root}/`
    return candidate.startsWith(prefix) ? candidate.slice(prefix.length) : null
}

export function isPathInsideDirectory(directory: string, path: string): boolean {
    if (!directory) return false
    const root = normalizeForComparison(directory)
    const candidate = normalizeForComparison(path)
    return relativeFromNormalizedRoot(root, candidate) !== null
}

export function relativePathWithin(directory: string, path: string): string | null {
    if (!directory) return null
    const root = normalizeForComparison(directory)
    const candidate = normalizeForComparison(path)
    return relativeFromNormalizedRoot(root, candidate)
}

/**
 * Formats a path for display. Home-relative and repo-relative forms keep the
 * root visible; only genuinely long paths elide, and they elide the middle so
 * the reader can still tell which tree the file lives in.
 */
export function formatPathReferenceLabel(path: string, options: { line?: number | null; workingDir?: string } = {}): string {
    const label = withLineSuffix(shortenPathForLabel(path, options.workingDir), options.line)
    return label
}

function withLineSuffix(label: string, line?: number | null): string {
    return line ? `${label}:${line}` : label
}

function shortenPathForLabel(path: string, workingDir?: string): string {
    if (!isAbsolutePathReference(path)) return path

    const relative = workingDir ? relativePathWithin(workingDir, path) : null
    if (relative && relative !== ".") return elideMiddle(relative, false)

    const { homeDir } = getPlatformInfo()
    if (homeDir && homeDir !== "/" && isPathInsideDirectory(homeDir, path)) {
        const fromHome = relativePathWithin(homeDir, path)
        if (fromHome && fromHome !== ".") return `~/${elideMiddle(fromHome, false)}`
    }

    return elideMiddle(path, true)
}

function elideMiddle(path: string, absolute: boolean): string {
    const segments = splitPath(path)
    if (segments.length <= LABEL_MAX_SEGMENTS) return path
    const head = segments[0]
    const tail = segments.slice(-LABEL_TAIL_SEGMENTS).join("/")
    return `${absolute ? "/" : ""}${head}/…/${tail}`
}
